import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Container, Row, Col, Button, Label, Input,
} from "reactstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import ReactSelect from "components/Form/Select/Select";
import Loading from "lib/loading";
import rotas from "services/rotas";
import rest from "lib/rest";

const formSwal = withReactContent(Swal);

/* ─── helpers ─── */
const fmtNum = (v) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const fmtInt = (v) => new Intl.NumberFormat("pt-BR").format(v || 0);

const TIPO_FILTRO_OPTS = [
  { id: "TODOS",       title: "Todos os produtos" },
  { id: "CATEGORIA",   title: "Categoria"         },
  { id: "SUBCATEGORIA",title: "Subcategoria"      },
  { id: "FORNECEDOR",  title: "Fornecedor"        },
];

const PRECO_BASE_OPTS = [
  { value: "MENOR",   label: "Menor preço"      },
  { value: "PROMOCAO",label: "Preço de Promoção" },
  { value: "VENDA",   label: "Preço de Venda"   },
];

/* ══════════════════════════════════════════ */
const ProdutosCampanha = () => {

  /* ── dados da campanha (passados via sessionStorage) ── */
  const campanhaId   = sessionStorage.getItem("campanhaEmMassa_id")   || "";
  const campanhaNome = sessionStorage.getItem("campanhaEmMassa_nome")  || "Campanha";

  /* ── lojas ── */
  const [lojas,    setLojas]   = useState([]);
  const [filial,   setFilial]  = useState(null);
  const [loadInit, setLoadInit]= useState(true);

  /* ── filtros ── */
  const [tipoFiltro,   setTipoFiltro]   = useState(TIPO_FILTRO_OPTS[0]);
  const [filtroOpcoes, setFiltroOpcoes] = useState([]);
  const [filtroId,     setFiltroId]     = useState(null);
  const [apenasComSaldo, setApenasComSaldo] = useState(true);
  const [liberados,    setLiberados]    = useState(true);
  const [controlados,  setControlados]  = useState(true);
  const [vencerAtivo,  setVencerAtivo]  = useState(false);
  const [vencerDias,   setVencerDias]   = useState(30);
  const [precoBase,    setPrecoBase]    = useState("MENOR");

  /* ── resultados ── */
  const [lista,       setLista]       = useState([]);
  const [prCampanha,  setPrCampanha]  = useState({}); // { produto_id: valor }
  const [selecionados,setSelecionados]= useState(new Set());
  const [apenasMarcados, setApenasMarcados] = useState(false);

  /* ── alteração em lote ── */
  const [alterarTipo, setAlterarTipo]   = useState("FIXO");
  const [alterarValor,setAlterarValor]  = useState("");

  /* ── status ── */
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingSave,   setLoadingSave]   = useState(false);
  const [pesquisou,     setPesquisou]     = useState(false);

  /* ─── Carrega lojas ─── */
  useEffect(() => {
    if (!campanhaId) {
      formSwal.fire("Atenção", "Nenhuma campanha selecionada. Volte e tente novamente.", "warning");
      return;
    }
    rest.get(rotas.lojas).then((ret) => {
      if (ret.success === "S" && ret.lista) {
        setLojas(ret.lista.map((l) => ({ id: l.filial_id, title: l.fantasia })));
      }
      setLoadInit(false);
    });
  }, []); // eslint-disable-line

  /* ─── Carrega opções do segundo dropdown ─── */
  const carregarFiltroOpcoes = useCallback(async (tipo) => {
    setFiltroId(null);
    setFiltroOpcoes([]);
    if (tipo === "TODOS") return;

    try {
      let ret;
      if (tipo === "CATEGORIA") {
        ret = await rest.get(rotas.categoriaSelect);
        if (ret.success === "S" && ret.lista) {
          setFiltroOpcoes(ret.lista.map((c) => ({ id: c.categoria_id, title: c.categoria })));
        }
      } else if (tipo === "SUBCATEGORIA") {
        ret = await rest.get(rotas.subcategoria);
        if (ret.success === "S" && ret.lista) {
          setFiltroOpcoes(ret.lista.map((s) => ({ id: s.subcategoria_id, title: s.subcategoria })));
        }
      } else if (tipo === "FORNECEDOR") {
        ret = await rest.get(rotas.fornecedor);
        if (ret.success === "S" && ret.lista) {
          setFiltroOpcoes(ret.lista.map((f) => ({ id: f.fornecedor_id, title: f.fornecedor || f.razao_social })));
        }
      }
    } catch (e) {
      console.error("Erro ao carregar opções de filtro:", e);
    }
  }, []);

  const handleTipoFiltroChange = (opt) => {
    setTipoFiltro(opt);
    carregarFiltroOpcoes(opt.id);
  };

  /* ─── Pesquisar ─── */
  const pesquisar = () => {
    if (!filial) {
      formSwal.fire("Atenção", "Selecione uma loja antes de pesquisar.", "warning");
      return;
    }
    if (!liberados && !controlados) {
      formSwal.fire("Atenção", "Selecione ao menos um tipo de produto (Liberados ou Controlados).", "warning");
      return;
    }

    setLoadingSearch(true);
    setSelecionados(new Set());
    setPesquisou(false);

    rest.post(rotas.campanhaPesquisaMassa, {
      form: {
        filial_id:    filial.id,
        tipo_filtro:  tipoFiltro.id,
        filtro_id:    filtroId?.id || 0,
        saldo:        apenasComSaldo ? "S" : "N",
        liberados:    liberados    ? "S" : "N",
        controlados:  controlados  ? "S" : "N",
        vencer_dias:  vencerAtivo  ? vencerDias : 0,
        preco_base:   precoBase,
        campanha_id:  campanhaId,
      },
    }).then((ret) => {
      setLoadingSearch(false);
      setPesquisou(true);
      if (ret.success === "S") {
        const nova = ret.lista || [];
        setLista(nova);
        /* inicializa preços de campanha */
        const precos = {};
        nova.forEach((p) => { precos[p.produto_id] = p.pr_campanha; });
        setPrCampanha(precos);
      } else {
        formSwal.fire("Erro", ret.msg || "Erro ao pesquisar produtos.", "error");
      }
    }).catch(() => {
      setLoadingSearch(false);
      formSwal.fire("Erro", "Falha na comunicação.", "error");
    });
  };

  /* ─── Seleção ─── */
  const toggleSelecionado = (id) =>
    setSelecionados((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const marcarTodos = () => {
    const visiveis = apenasMarcados ? [...selecionados] : lista.map((p) => p.produto_id);
    setSelecionados(new Set(visiveis));
  };

  const desmarcarTodos = () => setSelecionados(new Set());

  /* ─── Alteração em lote ─── */
  const aplicarAlteracao = () => {
    const val = parseFloat(String(alterarValor).replace(",", ".")) || 0;
    if (val <= 0) {
      formSwal.fire("Atenção", "Informe um valor maior que zero.", "warning");
      return;
    }
    if (selecionados.size === 0) {
      formSwal.fire("Atenção", "Selecione ao menos um produto.", "warning");
      return;
    }

    setPrCampanha((prev) => {
      const next = { ...prev };
      selecionados.forEach((id) => {
        const atual = parseFloat(next[id]) || 0;
        if (alterarTipo === "FIXO") {
          next[id] = Math.max(0, atual - val);
        } else {
          next[id] = Math.max(0, atual - (atual * val) / 100);
        }
        next[id] = Math.round(next[id] * 100) / 100;
      });
      return next;
    });
  };

  /* ─── Salvar ─── */
  const salvar = () => {
    if (selecionados.size === 0) {
      formSwal.fire("Atenção", "Selecione ao menos um produto para salvar.", "warning");
      return;
    }

    const itens = lista
      .filter((p) => selecionados.has(p.produto_id))
      .map((p) => ({
        produto_id:        p.produto_id,
        campanha_itens_id: p.campanha_itens_id || 0,
        valor:             parseFloat(prCampanha[p.produto_id]) || 0,
      }))
      .filter((i) => i.valor > 0);

    if (!itens.length) {
      formSwal.fire("Atenção", "Todos os produtos selecionados estão com valor zerado.", "warning");
      return;
    }

    setLoadingSave(true);
    rest.post(rotas.campanhaInserirEmLote, {
      form: { campanha_id: campanhaId, filial_id: filial.id, itens },
    }).then((ret) => {
      setLoadingSave(false);
      if (ret.success === "S") {
        formSwal.fire(
          "Sucesso!",
          `${ret.qtd_inseridos} inserido(s) · ${ret.qtd_atualizados} atualizado(s)`,
          "success"
        );
        /* re-pesquisa para atualizar campanha_itens_id */
        pesquisar();
      } else {
        formSwal.fire("Erro", ret.msg || "Erro ao salvar.", "error");
      }
    }).catch(() => {
      setLoadingSave(false);
      formSwal.fire("Erro", "Falha na comunicação.", "error");
    });
  };

  /* ─── Lista visível ─── */
  const listaVisivel = apenasMarcados
    ? lista.filter((p) => selecionados.has(p.produto_id))
    : lista;

  /* ─── Render ─── */
  if (loadInit) return <div className="content"><Loading tipo="componente" /></div>;

  return (
    <div className="content">
      <style>{`
        .pm-tr-selecionado td { background: #fff8f8 !important; }
        .pm-tr:hover td { background: #f8fafc !important; cursor: pointer; }
        .pm-preco-input {
          width: 90px; text-align: right; border: 1px solid #e2e8f0;
          border-radius: 5px; padding: 3px 6px; font-size: 12px;
          font-weight: 700; color: #065f46; outline: none;
        }
        .pm-preco-input:focus { border-color: #e74c3c; }
        .pm-check { width: 16px; height: 16px; cursor: pointer; accent-color: #e74c3c; }
      `}</style>

      <Container fluid>

        {/* ── Cabeçalho ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button
            type="button"
            onClick={() => window.history.back()}
            style={{
              background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 7,
              padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "#475569",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <i className="fa fa-arrow-left" /> Voltar
          </button>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#2c3e50" }}>
              Produtos em Massa
            </div>
            <div style={{ fontSize: 12, color: "#7f8c8d" }}>
              Campanha: <strong>{campanhaNome}</strong>
              {filial && <> · Loja: <strong>{filial.title}</strong></>}
            </div>
          </div>
        </div>

        {/* ── Filtros ── */}
        <Card className="mb-3">
          <CardBody style={{ padding: "16px 20px" }}>

            <Row className="align-items-end mb-2">

              {/* Loja */}
              <Col md="3" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Loja <span style={{ color: "#e74c3c" }}>*</span>
                </Label>
                <ReactSelect
                  name="filial" placeholder="Selecione..."
                  options={lojas} value={filial}
                  onChange={(v) => { setFilial(v); setLista([]); setPesquisou(false); }}
                />
              </Col>

              {/* Tipo de filtro */}
              <Col md="2" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Tipo de filtro
                </Label>
                <ReactSelect
                  name="tipoFiltro" options={TIPO_FILTRO_OPTS}
                  value={tipoFiltro} onChange={handleTipoFiltroChange}
                />
              </Col>

              {/* Segundo filtro */}
              <Col md="3" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  {tipoFiltro.id === "TODOS" ? "—" : tipoFiltro.title}
                </Label>
                <ReactSelect
                  name="filtroId" placeholder="Todos..."
                  options={filtroOpcoes} value={filtroId}
                  onChange={(v) => setFiltroId(v)}
                  isDisabled={tipoFiltro.id === "TODOS"}
                />
              </Col>

              {/* Saldo */}
              <Col md="2" className="mb-2 d-flex align-items-center pt-2">
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12.5 }}>
                  <input
                    type="checkbox" className="pm-check"
                    checked={apenasComSaldo}
                    onChange={(e) => setApenasComSaldo(e.target.checked)}
                  />
                  Apenas saldo &gt; 0
                </label>
              </Col>

              {/* Pesquisar */}
              <Col md="2" className="mb-2">
                <Button
                  color="danger" block
                  onClick={pesquisar}
                  disabled={loadingSearch || !filial}
                  style={{ height: 38 }}
                >
                  {loadingSearch
                    ? <><i className="fa fa-spinner fa-spin mr-1" />Buscando...</>
                    : <><i className="fa fa-search mr-1" />Pesquisar</>}
                </Button>
              </Col>
            </Row>

            {/* Linha 2: tipo produto + preço base */}
            <Row className="align-items-center">
              <Col md="6">
                <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#7f8c8d", textTransform: "uppercase" }}>
                    Tipo de produto:
                  </span>
                  {[
                    { key: "liberados",   label: "Liberados",   state: liberados,   set: setLiberados   },
                    { key: "controlados", label: "Controlados", state: controlados, set: setControlados },
                  ].map(({ key, label, state, set }) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12.5 }}>
                      <input type="checkbox" className="pm-check" checked={state} onChange={(e) => set(e.target.checked)} />
                      {label}
                    </label>
                  ))}
                  <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12.5 }}>
                    <input type="checkbox" className="pm-check" checked={vencerAtivo} onChange={(e) => setVencerAtivo(e.target.checked)} />
                    A vencer em
                    <input
                      type="number" min={1} max={365}
                      value={vencerDias}
                      onChange={(e) => setVencerDias(parseInt(e.target.value) || 30)}
                      disabled={!vencerAtivo}
                      style={{ width: 46, border: "1px solid #e2e8f0", borderRadius: 5, padding: "2px 6px", fontSize: 12, textAlign: "center" }}
                    />
                    dias
                  </label>
                </div>
              </Col>

              <Col md="6">
                <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#7f8c8d", textTransform: "uppercase" }}>
                    Preço base:
                  </span>
                  {PRECO_BASE_OPTS.map((opt) => (
                    <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12.5 }}>
                      <input
                        type="radio" name="precoBase"
                        style={{ accentColor: "#e74c3c", cursor: "pointer" }}
                        checked={precoBase === opt.value}
                        onChange={() => setPrecoBase(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </Col>
            </Row>

          </CardBody>
        </Card>

        {/* ── Tabela de resultados ── */}
        {(pesquisou || loadingSearch) && (
          <Card>
            <CardHeader>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CardTitle tag="h6" className="mb-0">Produtos encontrados</CardTitle>
                  {lista.length > 0 && (
                    <span style={{ fontSize: 12, color: "#7f8c8d" }}>
                      {lista.length} produto{lista.length !== 1 ? "s" : ""}
                      {selecionados.size > 0 && (
                        <span style={{ marginLeft: 8, color: "#e74c3c", fontWeight: 700 }}>
                          · {selecionados.size} selecionado{selecionados.size !== 1 ? "s" : ""}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {lista.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, cursor: "pointer" }}>
                      <input type="checkbox" className="pm-check" checked={apenasMarcados} onChange={(e) => setApenasMarcados(e.target.checked)} />
                      Apenas marcados
                    </label>
                    <button type="button" onClick={marcarTodos}
                      style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 5, padding: "4px 12px", fontSize: 11.5, cursor: "pointer", fontWeight: 600 }}>
                      Marcar todos
                    </button>
                    <button type="button" onClick={desmarcarTodos}
                      style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 5, padding: "4px 12px", fontSize: 11.5, cursor: "pointer", fontWeight: 600 }}>
                      Desmarcar todos
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardBody className="p-0" style={{ position: "relative", minHeight: 120 }}>
              {loadingSearch ? (
                <div style={{ padding: "60px 0", textAlign: "center" }}><Loading tipo="componente" /></div>
              ) : listaVisivel.length === 0 ? (
                <div style={{ textAlign: "center", padding: "50px 0", color: "#7f8c8d" }}>
                  <i className="fa fa-search" style={{ fontSize: 28, display: "block", marginBottom: 8, color: "#dfe6e9" }} />
                  {apenasMarcados ? "Nenhum produto marcado." : "Nenhum produto encontrado com os filtros selecionados."}
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "55vh", overflowY: "auto" }}>
                  <table className="table" style={{ marginBottom: 0, fontSize: 12.5 }}>
                    <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: 38, textAlign: "center" }}>
                          <input
                            type="checkbox" className="pm-check"
                            checked={selecionados.size > 0 && listaVisivel.every((p) => selecionados.has(p.produto_id))}
                            onChange={(e) => e.target.checked
                              ? setSelecionados((prev) => { const n = new Set(prev); listaVisivel.forEach((p) => n.add(p.produto_id)); return n; })
                              : setSelecionados((prev) => { const n = new Set(prev); listaVisivel.forEach((p) => n.delete(p.produto_id)); return n; })
                            }
                          />
                        </th>
                        <th style={{ width: 70 }}>Código</th>
                        <th>Produto</th>
                        <th style={{ width: 70, textAlign: "center" }}>Saldo</th>
                        <th style={{ width: 55, textAlign: "center" }}>Curva</th>
                        <th style={{ width: 100, textAlign: "right" }}>Últ. Compra</th>
                        <th style={{ width: 100, textAlign: "right" }}>Pr. Venda</th>
                        <th style={{ width: 100, textAlign: "right" }}>Pr. Promoção</th>
                        <th style={{ width: 115, textAlign: "right" }}>Pr. Campanha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaVisivel.map((prod, idx) => {
                        const sel = selecionados.has(prod.produto_id);
                        return (
                          <tr
                            key={prod.produto_id}
                            className={`pm-tr${sel ? " pm-tr-selecionado" : ""}`}
                            onClick={() => toggleSelecionado(prod.produto_id)}
                          >
                            <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox" className="pm-check"
                                checked={sel}
                                onChange={() => toggleSelecionado(prod.produto_id)}
                              />
                            </td>
                            <td style={{ color: "#94a3b8", fontSize: 11 }}>{prod.produto_id}</td>
                            <td>
                              <div style={{ fontWeight: 600, color: "#2c3e50" }}>{prod.produto}</div>
                              {prod.cod_barras && (
                                <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{prod.cod_barras}</div>
                              )}
                            </td>
                            <td style={{ textAlign: "center", fontWeight: 700, color: prod.saldo > 0 ? "#065f46" : "#94a3b8" }}>
                              {fmtInt(prod.saldo)}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {prod.curva_classe ? (
                                <span style={{
                                  display: "inline-block", padding: "1px 7px", borderRadius: 4,
                                  fontSize: 11, fontWeight: 800,
                                  background: { A: "#ECFDF5", B: "#FFFBEB", C: "#FEF2F2", D: "#f3f4f6" }[prod.curva_classe] || "#f3f4f6",
                                  color:      { A: "#065f46", B: "#92400e", C: "#991b1b", D: "#374151" }[prod.curva_classe] || "#374151",
                                }}>
                                  {prod.curva_classe}
                                </span>
                              ) : "—"}
                            </td>
                            <td style={{ textAlign: "right", color: "#64748b" }}>
                              {prod.ult_compra > 0 ? `R$ ${fmtNum(prod.ult_compra)}` : "—"}
                            </td>
                            <td style={{ textAlign: "right", color: "#2c3e50" }}>
                              R$ {fmtNum(prod.pr_venda)}
                            </td>
                            <td style={{ textAlign: "right", color: prod.pr_promocao > 0 ? "#0369a1" : "#94a3b8" }}>
                              {prod.pr_promocao > 0 ? `R$ ${fmtNum(prod.pr_promocao)}` : "—"}
                            </td>
                            <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>R$</span>
                                <input
                                  type="number" min={0} step="0.01"
                                  className="pm-preco-input"
                                  value={prCampanha[prod.produto_id] ?? prod.pr_campanha}
                                  onChange={(e) =>
                                    setPrCampanha((prev) => ({
                                      ...prev,
                                      [prod.produto_id]: parseFloat(e.target.value) || 0,
                                    }))
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>

            {/* ── Rodapé: alterar valores + salvar ── */}
            {lista.length > 0 && !loadingSearch && (
              <div style={{
                padding: "14px 20px",
                background: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 12,
              }}>

                {/* Alterar em lote */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#7f8c8d", textTransform: "uppercase" }}>
                    Alterar valores ({selecionados.size} selecionado{selecionados.size !== 1 ? "s" : ""}):
                  </span>
                  {["FIXO", "PERCENTUAL"].map((tipo) => (
                    <label key={tipo} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12.5, cursor: "pointer" }}>
                      <input
                        type="radio" name="alterarTipo"
                        style={{ accentColor: "#e74c3c", cursor: "pointer" }}
                        checked={alterarTipo === tipo}
                        onChange={() => setAlterarTipo(tipo)}
                      />
                      {tipo === "FIXO" ? "Diminuir valor fixo" : "Diminuir percentual"}
                    </label>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {alterarTipo === "FIXO" && <span style={{ fontSize: 11, color: "#94a3b8" }}>R$</span>}
                    <input
                      type="number" min={0} step={alterarTipo === "FIXO" ? "0.01" : "1"}
                      value={alterarValor}
                      onChange={(e) => setAlterarValor(e.target.value)}
                      placeholder="0"
                      style={{
                        width: 80, border: "1px solid #e2e8f0", borderRadius: 6,
                        padding: "5px 8px", fontSize: 13, textAlign: "right", outline: "none",
                      }}
                    />
                    {alterarTipo === "PERCENTUAL" && <span style={{ fontSize: 11, color: "#94a3b8" }}>%</span>}
                  </div>
                  <button
                    type="button"
                    onClick={aplicarAlteracao}
                    disabled={selecionados.size === 0}
                    style={{
                      background: "#e74c3c", color: "#fff", border: "none",
                      borderRadius: 6, padding: "6px 14px", fontSize: 12.5,
                      fontWeight: 700, cursor: selecionados.size > 0 ? "pointer" : "not-allowed",
                      opacity: selecionados.size > 0 ? 1 : 0.5,
                    }}
                  >
                    Aplicar
                  </button>
                </div>

                {/* Salvar */}
                <Button
                  color="success"
                  onClick={salvar}
                  disabled={loadingSave || selecionados.size === 0}
                  style={{ minWidth: 160 }}
                >
                  {loadingSave
                    ? <><i className="fa fa-spinner fa-spin mr-1" />Salvando...</>
                    : <><i className="fa fa-check mr-1" />Salvar {selecionados.size > 0 ? `(${selecionados.size})` : ""}</>}
                </Button>

              </div>
            )}

          </Card>
        )}

        {/* Estado inicial */}
        {!pesquisou && !loadingSearch && (
          <Card body className="text-center py-5">
            <i className="fa fa-filter" style={{ fontSize: 32, color: "#dfe6e9", display: "block", marginBottom: 10 }} />
            <div style={{ color: "#7f8c8d", fontSize: 13 }}>
              {!filial
                ? "Selecione uma loja e clique em Pesquisar para começar."
                : "Configure os filtros e clique em Pesquisar."}
            </div>
          </Card>
        )}

      </Container>
    </div>
  );
};

export default ProdutosCampanha;
